import { IAlphaPendingConfirmation, IAlphaRequestInFlight } from 'store/alpha/types';
import { AlphaEvent } from 'services/alpha/types';
import { pendingCardView } from 'services/alpha/pendingConfirmations';
import { withDefaults } from 'components/Alpha/cards/AlphaFieldRenderer';
import { CardDraft, readCardDraft } from 'components/Alpha/cards/cardDraft';
import { changedKeys, flattenFields, isRecord } from 'components/Alpha/cards/cardModel';
import { CardServerError, FormField } from 'components/Alpha/cards/cardTypes';
import { ANOTHER_REQUEST_TEXT } from 'components/Alpha/cards/useCardActions';

type CardErrorEvent = Extract<AlphaEvent, { type: 'card_error' }>;

export const STILL_WORKING_TEXT = 'Alpha is still working on this reply.';
export const SWITCHING_TEXT = 'Alpha is opening the chat you picked.';

export const cardFields = (pending: IAlphaPendingConfirmation): FormField[] => {
  const { card } = pending;
  const view = pendingCardView(pending);
  if (!card || view === 'confirm') return [];
  if (view === 'editable') return card.editableFields ?? [];
  if (card.form) return flattenFields(card.form);
  return view === 'ask' ? card.editableFields ?? [] : [];
};

const shownBaseline = (pending: IAlphaPendingConfirmation): Record<string, unknown> =>
  withDefaults(cardFields(pending), isRecord(pending.card?.values) ? pending.card.values : {});

export const shownCardValues = (
  token: string,
  pending: IAlphaPendingConfirmation | undefined,
): Record<string, unknown> | undefined =>
  readCardDraft(token)?.values ?? (pending ? shownBaseline(pending) : undefined);

export const cardErrorFromEvent = (
  event: CardErrorEvent,
  pendingConfirmations: IAlphaPendingConfirmation[],
): { token: string; error: CardServerError } => ({
  token: event.token,
  error: {
    message: event.message,
    fieldErrors: event.fieldErrors ?? {},
    values: shownCardValues(
      event.token,
      pendingConfirmations.find(card => card.token === event.token),
    ),
  },
});

export const blockedByRequest = (
  request: IAlphaRequestInFlight | undefined,
  token: string,
): boolean => request !== undefined && request.token !== token;

export const blockedNoteFor = (
  request: IAlphaRequestInFlight | undefined,
  token: string,
): string | undefined => {
  if (!blockedByRequest(request, token)) return undefined;
  return request?.cards?.includes(token) ? STILL_WORKING_TEXT : ANOTHER_REQUEST_TEXT;
};

export const hasUnsentEdits = (pending: IAlphaPendingConfirmation): boolean => {
  const view = pendingCardView(pending);
  const draft = readCardDraft(pending.token);
  if (!pending.card || !draft || (view !== 'form' && view !== 'editable')) return false;
  return changedKeys(shownBaseline(pending), draft.values, cardFields(pending)).length > 0;
};

export const withHeldText = (current: string, held: string): string => {
  const kept = current.trim();
  if (kept === '') return held;
  if (kept === held.trim()) return current;
  return `${current}\n${held}`;
};

export const draftFingerprint = (draft: CardDraft | undefined): string =>
  draft === undefined ? '' : JSON.stringify(draft);

export const collectCardDrafts = (
  pending: IAlphaPendingConfirmation[],
  snapshots: ReadonlyMap<string, string>,
): Record<string, CardDraft> | undefined => {
  const entries = pending
    .map((card): [string, CardDraft | undefined] => [card.token, readCardDraft(card.token)])
    .filter(
      (entry): entry is [string, CardDraft] =>
        entry[1] !== undefined && draftFingerprint(entry[1]) !== snapshots.get(entry[0]),
    );
  return entries.length ? Object.fromEntries(entries) : undefined;
};
